// LearnedKeywordsManager.js
// Manages learning and storage of subscription keywords in Firebase

import { collection, getDocs, addDoc, query, where, Timestamp, doc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';

export class LearnedKeywordsManager {
  /**
   * Get all learned keywords for a user
   */
  static async getLearnedKeywords(userId) {
    try {
      const keywordsRef = collection(db, 'users', userId, 'learned_keywords');
      const snapshot = await getDocs(keywordsRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error loading learned keywords:', error);
      return [];
    }
  }

  /**
   * Get just the keyword strings (for detection)
   */
  static async getLearnedKeywordStrings(userId) {
    const keywords = await this.getLearnedKeywords(userId);
    return keywords.map(k => k.keyword);
  }

  /**
   * Add a new learned keyword
   */
  static async addLearnedKeyword(userId, keyword, serviceName, merchantName) {
    try {
      // Check if already exists
      const existing = await this.getLearnedKeywords(userId);
      const keywordLower = keyword.toLowerCase().trim();
      
      if (existing.some(k => k.keyword === keywordLower)) {
        console.log('Keyword already learned:', keywordLower);
        
        // Increment usage count
        const existingDoc = existing.find(k => k.keyword === keywordLower);
        // Could update here, but for now just skip
        return { success: true, alreadyExists: true };
      }

      // Extract additional keywords from merchant name
      const additionalKeywords = this.extractKeywords(merchantName);
      
      // Add new keyword
      const keywordsRef = collection(db, 'users', userId, 'learned_keywords');
      await addDoc(keywordsRef, {
        keyword: keywordLower,
        serviceName: serviceName,
        merchantName: merchantName,
        additionalKeywords: additionalKeywords,
        addedDate: Timestamp.now(),
        confidence: 1.0,
        timesDetected: 1,
        lastUsed: Timestamp.now()
      });

      console.log('✓ Learned new keyword:', keywordLower);
      return { success: true, keyword: keywordLower };
    } catch (error) {
      console.error('Error adding learned keyword:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Extract potential keywords from merchant name
   */
  static extractKeywords(merchantName) {
    if (!merchantName) return [];
    
    const cleaned = merchantName
      .toLowerCase()
      .replace(/\*+/g, ' ')
      .replace(/\d{10,}/g, '') // Remove phone numbers
      .replace(/\b\d{3,4}\b/g, '') // Remove codes
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && word.length < 15); // Reasonable word length
    
    // Return unique words, up to 3
    const unique = [...new Set(cleaned)];
    return unique.slice(0, 3);
  }

  /**
   * Check if a merchant matches any learned keywords
   */
  static matchesLearnedKeywords(merchantName, learnedKeywords) {
    const merchant = String(merchantName || '').toLowerCase();
    if (!merchant) return { matched: false };
    
    for (const learned of learnedKeywords) {
      // Check main keyword
      if (learned?.keyword && merchant.includes(String(learned.keyword).toLowerCase())) {
        return { matched: true, keyword: learned };
      }
      
      // Check additional keywords
      if (learned.additionalKeywords) {
        for (const additional of learned.additionalKeywords) {
          if (additional && merchant.includes(String(additional).toLowerCase())) {
            return { matched: true, keyword: learned };
          }
        }
      }
    }
    
    return { matched: false };
  }

  /**
   * Delete a learned keyword
   */
  static async deleteLearnedKeyword(userId, keywordId) {
    try {
      const docRef = doc(db, 'users', userId, 'learned_keywords', keywordId);
      await deleteDoc(docRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting keyword:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete learned keywords by service name
   */
  static async deleteLearnedKeywordsForService(userId, serviceName) {
    try {
      const keywordsRef = collection(db, 'users', userId, 'learned_keywords');
      const q = query(keywordsRef, where('serviceName', '==', serviceName));
      const snapshot = await getDocs(q);

      const deletePromises = [];
      snapshot.forEach((docSnap) => {
        deletePromises.push(deleteDoc(docSnap.ref));
      });

      await Promise.all(deletePromises);
      return { success: true, count: deletePromises.length };
    } catch (error) {
      console.error('Error deleting keywords for service:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get statistics about learned keywords
   */
  static async getStats(userId) {
    const keywords = await this.getLearnedKeywords(userId);
    
    return {
      total: keywords.length,
      mostRecent: keywords.sort((a, b) => b.addedDate - a.addedDate)[0],
      totalDetections: keywords.reduce((sum, k) => sum + (k.timesDetected || 1), 0)
    };
  }
}

export default LearnedKeywordsManager;
